// The Kite connector's admin side (/v1/admin/connector*): Claude and ChatGPT, each with its own
// company switch, each member's allow for each, and live connections (DESIGN.md, "Claude and
// ChatGPT for reps").

import { queryOptions } from "@tanstack/react-query";
import { api } from "./client";

/** The AI providers reps can connect to Kite. */
export type ConnectorProvider = "claude" | "chatgpt";

/** Claude first, as the server lists them. */
export const CONNECTOR_PROVIDERS: readonly ConnectorProvider[] = ["claude", "chatgpt"];

interface ProviderMeta {
  name: string;
  /** Who keeps the data under the rep's account, for the data notice. */
  vendor: string;
}

export const PROVIDER_META: Record<ConnectorProvider, ProviderMeta> = {
  claude: { name: "Claude", vendor: "Anthropic" },
  chatgpt: { name: "ChatGPT", vendor: "OpenAI" },
};

/** A member named in the connector views. */
export interface ConnectorPerson {
  user_id: string;
  name: string;
  email: string;
}

/** One provider's company switch. */
export interface ProviderSettings {
  provider: ConnectorProvider;
  name: string;
  enabled: boolean;
  /** When the switch was last set; absent when it never was. */
  updated_at: string | null;
  updated_by: ConnectorPerson | null;
  notice_version: string | null;
  /** Live connections with this provider. */
  live_grants: number;
}

/** A member's allow for one provider. */
export interface MemberProvider {
  provider: ConnectorProvider;
  allowed: boolean;
  live_grants: number;
}

/** One member and their allows. */
export interface ConnectorMember {
  user_id: string;
  email: string;
  name: string;
  /** Allowed any provider. */
  allowed: boolean;
  providers: MemberProvider[];
  /** Live connections this member has. */
  live_grants: number;
  /** `active` or `invited`; disabled members are left out of the list. */
  state?: "active" | "invited" | "disabled";
}

/** One live connection: a rep's Claude or ChatGPT. */
export interface AdminConnection {
  id: string;
  user: ConnectorPerson;
  /** As the client registered it: "Claude" or "ChatGPT". */
  client_name: string;
  provider: ConnectorProvider;
  created_at: string;
  last_used_at: string | null;
}

/** GET /v1/admin/connector */
export interface ConnectorSettings {
  /** Any provider on. */
  enabled: boolean;
  updated_at: string | null;
  updated_by: ConnectorPerson | null;
  live_grants: number;
  connector_url: string;
  /** Claude first. */
  providers: ProviderSettings[];
  members: ConnectorMember[];
  /** Most recently used first. */
  connections: AdminConnection[];
}

/** The data notice the admin agrees to when turning a provider on (DESIGN.md). It names only
 * that provider's vendor, so it is a new version from the shared one of 2026-10-21. */
export const NOTICE_VERSION = "2026-10-22";

export const connectorApi = {
  get: () => api<ConnectorSettings>("/v1/admin/connector"),
  setEnabled: (provider: ConnectorProvider, enabled: boolean) =>
    api<ConnectorSettings>("/v1/admin/connector", {
      method: "PUT",
      body: enabled ? { provider, enabled, notice_version: NOTICE_VERSION } : { provider, enabled },
    }),
  setMember: (userId: string, provider: ConnectorProvider, allowed: boolean) =>
    api<ConnectorMember>(`/v1/admin/connector/members/${encodeURIComponent(userId)}`, {
      method: "PUT",
      body: { provider, allowed },
    }),
  revoke: (grantId: string) =>
    api<void>(`/v1/admin/connector/grants/${encodeURIComponent(grantId)}`, { method: "DELETE" }),
};

export const connectorKey = ["admin", "connector"] as const;

export const connectorQuery = queryOptions({
  queryKey: connectorKey,
  queryFn: connectorApi.get,
});

/** One provider's switch (off and never set when the server has no row for it). */
export function providerOf(v: ConnectorSettings, p: ConnectorProvider): ProviderSettings {
  return (
    v.providers.find((x) => x.provider === p) ?? {
      provider: p,
      name: PROVIDER_META[p].name,
      enabled: false,
      updated_at: null,
      updated_by: null,
      notice_version: null,
      live_grants: 0,
    }
  );
}

/** One provider's live connections. */
export function connectionsOf(
  v: Pick<ConnectorSettings, "connections">,
  p: ConnectorProvider,
): AdminConnection[] {
  return v.connections.filter((c) => c.provider === p);
}

/** A member's allow and live connections for one provider. */
export function memberProvider(m: ConnectorMember, p: ConnectorProvider): MemberProvider {
  return m.providers.find((x) => x.provider === p) ?? { provider: p, allowed: false, live_grants: 0 };
}

/** "Oct 21" in the viewer's locale. */
export function shortDay(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** The card's second line: who set the switch last, and when. */
export function switchLine(s: Pick<ProviderSettings, "enabled" | "updated_at" | "updated_by">): string {
  if (!s.updated_at) return "Never turned on";
  const verb = s.enabled ? "Turned on" : "Turned off";
  return s.updated_by
    ? `${verb} by ${s.updated_by.name}, ${shortDay(s.updated_at)}`
    : `${verb}, ${shortDay(s.updated_at)}`;
}

/** The card's mono line: "3 connected, 2 reps" when on, "Off" when off. */
export function connectorSummary(s: { enabled: boolean; connections: AdminConnection[] }): string {
  if (!s.enabled) return "Off";
  const reps = new Set(s.connections.map((c) => c.user.user_id)).size;
  return `${s.connections.length} connected, ${reps} ${reps === 1 ? "rep" : "reps"}`;
}

/** "Dana" from "Dana Ruiz". */
export function firstName(name: string): string {
  return name.split(" ")[0] || name;
}
